import User from 'models/user';
import { apiGetUsersForCache } from 'services/Api/userService';
import { apiRequest, loginRequest } from 'services/Auth/authConfig';
import { graphGetUserPhoto } from 'services/Graph/graphServiceUser';
import logger from 'services/Logging/logService';
import { newGuidNil } from 'utils/guid';
import { EntityCache, ICache } from './entityCache';

export class UserCache extends EntityCache<User> implements ICache<User, UserCache> {
  private isLoading: boolean = false;

  private usersPromise: Promise<User[]> | undefined;

  constructor() {
    super();
    this.getId = (user) => user.id;
    this.getEmptyItem = (id) => User.getEmptyUser(id as string);
  }

  async getItems(refresh: boolean = false): Promise<User[]> {
    if (this.isLoading && this.usersPromise) {
      return await this.usersPromise;
    }

    if (this.items.length === 0 || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          this.isLoading = true;
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.usersPromise = apiGetUsersForCache(accessToken, this.appContext.globalDataCache);
          this.items = await this.usersPromise;
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.usersPromise = undefined;
          this.isLoading = false;
        }
      } else if (!this.appContext) {
        this.items = [];
      }
    }

    return this.items;
  }

  getPhotoURLObj(userId: string | undefined): string | undefined {
    if (!userId) return undefined;

    const user = this.get(userId);
    if (user.photo) {
      if (!user.photoURL) {
        user.photoURL = URL.createObjectURL(user.photo);
      }

      return user.photoURL;
    }

    return undefined;
  }

  async cachePhotos() {
    if (!this.appContext) return;

    //Not while a partner is viewing a customer. The users just loaded are the CUSTOMER's, but the Graph client
    //still belongs to the partner's own directory, so every lookup is for an object id that does not exist there:
    //one 404 and no photos. Impersonation is deliberately Graph free - see Security/Docs/PartnerCenter.md.
    if (this.appContext.isCustomerScopeActive()) return;

    //photos come from Microsoft Graph: a user of another identity provider has no token for it
    if (!this.appContext.hasMicrosoftIdentity()) return;

    const users = await this.getItems(false);
    const graphInterface = await this.appContext.getGraphInterface(loginRequest.scopes);

    // Limit the number of users to 100 to avoid performance issues
    const filteredUsers = users.filter((u) => !u.isAppType() && u.hasLicense && u.id !== newGuidNil());
    if (filteredUsers.length > 100) {
      return;
    }

    for (let i = 0; i < filteredUsers.length; i++) {
      const user = filteredUsers[i];
      if (!user.photo) {
        try {
          user.photo = await graphGetUserPhoto(graphInterface.client, user.id);
        } catch (err) {
          // If there an error retrieving the photo, ignore it and don't retrieve other photos
          logger.debug(`Error retrieving photo for user ${user.id}`, err);
          break;
        }
      }
    }
  }

  // Clear all photos from the cache
  async clearPhotos() {
    if (!this.appContext) return;

    const users = await this.getItems(true);
    for (const user of users) {
      user.photo = undefined;
      user.photoURL = undefined;
    }
  }

  clone(): UserCache {
    const userCache: UserCache = new UserCache();
    userCache.items = this.items?.map((u) => u.clone());

    return userCache;
  }
}
